import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { BookingQuestionsModal } from '../common/modals.js';
import { SEC_ApiCall, SEC_DataApiPost } from '../func.js';
import { CgSpinner } from 'react-icons/cg';
// Removed check icon; use styled status pill instead
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './bulkCancelModal.css';
import './bulkBookingModal.css';

// Props:
// - modalActive, setModalActive
// - groupId
// - seriesId
// - clicked: { roomId, lessonId, roomName, date, day, dateFormatted }
// - onComplete: callback to run after a successful cancellation (e.g., reload)
export default function BulkCancelModal({
    modalActive,
    setModalActive,
    groupId,
    seriesId,
    clicked,
    prefetchedOccurrences,
    onComplete
}) {
    const [loading, setLoading] = useState(false);
    const [occurrences, setOccurrences] = useState([]); // [{date, lessonKey, roomId, displayLesson, displayDate, selected, status}]
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Reset UI flags when modal opens/closes so checkboxes are re-enabled
    useEffect(() => {
        if (modalActive) {
            setSubmitted(false);
            setSubmitting(false);
        } else {
            setOccurrences([]);
            setLoading(false);
            setSubmitting(false);
            setSubmitted(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalActive]);

    // Load occurrences when opening
    useEffect(() => {
        async function loadSeries() {
            if (!modalActive || !seriesId) return;
            // If parent has already prefetched, use that to avoid duplicate loading
            if (Array.isArray(prefetchedOccurrences) && prefetchedOccurrences.length >= 0) {
                setOccurrences(prefetchedOccurrences);
                setLoading(false);
                return;
            }

            setLoading(true);
            setOccurrences([]);
            try {
                const params = { seriesId, group: groupId };
                const res = await SEC_ApiCall('bookings/BulkBooking', params);
                const items = (res.items || []).map(it => {
                    const isClicked = clicked && it.date === clicked.date && it.lessonKey === clicked.lessonId;
                    return {
                        ...it,
                        displayDate: moment(it.date, 'DDMMYYYY').format('DD/MM/YYYY'),
                        displayLesson: String(it.lessonKey || '').replace(/_/g, ' '),
                        selected: Boolean(isClicked),
                        status: null // 'ok' | 'error'
                    };
                });
                setOccurrences(items);
            } catch (e) {
                console.error('failed to load series', e);
                toast.error('❌ Failed to load bulk booking series', { autoClose: 4000, position: 'top-right' });
            } finally {
                setLoading(false);
            }
        }
        loadSeries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalActive, seriesId]);

    const toggleItem = (index) => {
        setOccurrences(prev => prev.map((it, idx) => idx === index ? { ...it, selected: !it.selected } : it));
    };

    const selectedCount = occurrences.filter(o => o.selected).length;

    const handleSubmit = async () => {
        if (selectedCount === 0) return;
        setSubmitting(true);
        try {
            const items = occurrences.filter(o => o.selected).map(o => ({
                date: o.date,
                lessonKey: o.lessonKey,
                roomId: o.roomId
            }));
            const payload = { group: groupId, seriesId, items };
            // Correct path per API: POST /bookings/BulkBooking/Cancel
            const res = await SEC_DataApiPost('bookings/BulkBooking/Cancel', JSON.stringify(payload));
            console.log('[BulkCancel] API response', res);

            // Detect backend-declared errors or empty payloads
            if (!res || res.data === 'error') {
                toast.error('❌ Bulk cancel failed', { autoClose: 4000, position: 'top-right' });
                return;
            }
            const results = Array.isArray(res.results) ? res.results : [];
            if (results.length === 0) {
                toast.error('❌ Bulk cancel failed', { autoClose: 4000, position: 'top-right' });
                return;
            }

            // Map results back to occurrences
            setOccurrences(prev => prev.map(o => {
                const r = results.find(x => x.date === o.date && x.lessonKey === o.lessonKey);
                if (!r) return o;
                const ok = (r.ok === true) || (r.status === 'success');
                return { ...o, status: ok ? 'ok' : 'error' };
            }));

            // Summarise and notify
            const okCount = results.filter(r => r && (r.ok === true || r.status === 'success')).length;
            const total = results.length;
            if (okCount === total) {
                toast.success(`✅ ${okCount} out of ${total} successfully cancelled`, { autoClose: 4000, position: 'top-right' });
            } else {
                const failed = total - okCount;
                toast.error(`❌ ${okCount} out of ${total} cancelled (${failed} failed)`, { autoClose: 5000, position: 'top-right' });
            }

            setSubmitted(true);
            if (typeof onComplete === 'function') onComplete();
        } catch (e) {
            console.error('cancel bulk failed', e);
            toast.error('❌ Bulk cancel failed', { autoClose: 4000, position: 'top-right' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <BookingQuestionsModal modalActiveState={modalActive} setStateFunction={setModalActive}>
            <div className='bulkCancelContainer'>
                <h2 className='bulkTitle'>Cancel Bulk Booking</h2>
                {clicked && (
                    <p className='bulkSubTitle'>{clicked.roomName} - {String(clicked.lessonId || '').replace(/_/g, ' ')} - {moment(clicked.date, 'DDMMYYYY').format('DD/MM/YYYY')}</p>
                )}

                {loading ? (
                    <div className='bulkLoading'>
                        <CgSpinner className='submit-button-spinner' />
                    </div>
                ) : (
                    <>
                        <p className='bulkHelpText'>Select the lessons to cancel from this series. Only the chosen lessons will be cancelled.</p>
                        <ul className='bulkCancelList'>
                            {occurrences.map((occ, idx) => (
                                <li key={`${occ.date}-${occ.lessonKey}`}>
                                    <label>
                                        <input
                                            type='checkbox'
                                            checked={occ.selected}
                                            onChange={() => toggleItem(idx)}
                                            disabled={submitted}
                                        />
                                        <span className='occLabel'>{occ.displayLesson} - {occ.displayDate}</span>
                                        {occ.status === 'ok' && (
                                            <span className='removedPill'>Booking Removed</span>
                                        )}
                                    </label>
                                </li>
                            ))}
                        </ul>
                        <div className='submitArea'>
                            <button
                                className='formSubmitButton'
                                onClick={submitted ? () => setModalActive(false) : handleSubmit}
                                disabled={!submitted && (submitting || selectedCount === 0)}
                            >
                                {submitted ? 'Close' : submitting ? <CgSpinner className='submit-button-spinner' /> : 'Submit'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </BookingQuestionsModal>
    );
}
